import type { V10ClientLinkViewProps } from "../types.js";
import { useState } from "react";

export function V10SummaryTab({
  shell,
  preview,
  active,
  isExternalProof,
  changeMessage,
  canAct,
  onApprove,
  onRequestChange,
}: V10ClientLinkViewProps & {
  readonly active: boolean;
  readonly canAct: boolean;
}) {
  const [consentAcknowledged, setConsentAcknowledged] = useState(false);
  const approveLabel = isExternalProof ? "Approve external proof" : "Approve preview";
  const headline = shell.headline || (isExternalProof ? "Your external proof review is ready." : "Your website preview is ready.");
  const pageCount = preview?.availableRoutes?.length ?? (preview?.previewRoute ? 1 : 0);
  const decisionLocked = !canAct;
  const canRequestChange = canAct && changeMessage.trim().length > 0;
  const canApprove = canAct && (isExternalProof || consentAcknowledged);
  return (
    <section
      className={`v10-cl-tab-panel${active ? " active" : ""}`}
      role="tabpanel"
      id="v10-tab-summary"
      aria-labelledby="v10-tab-btn-summary"
      hidden={!active}
    >
      <div className="v10-cl-decision-grid">
        <div>
          <p className="v10-cl-kicker">Your decision</p>
          <h1 className="v10-cl-headline">{headline}</h1>
          <p className="v10-cl-lede">
            {shell.subcopy ||
              "Review the preview, then approve it or send one clear change request to your agency."}
          </p>
          {pageCount > 1 ? (
            <p className="v10-cl-muted">
              Multi-page preview — {pageCount} pages available. Use the Preview tab to switch routes.
            </p>
          ) : null}
          {decisionLocked ? (
            <div className="v10-cl-actions" role="status">
              <span className="v10-cl-btn v10-cl-btn--gold" aria-disabled="true">
                Decision recorded
              </span>
              <p className="v10-cl-muted">Your agency will continue with the next proof-backed handoff step.</p>
            </div>
          ) : (
            <div className="v10-cl-actions">
              {!isExternalProof && preview?.previewVersionId ? (
                <p className="v10-cl-muted" data-testid="approval-version-ref">
                  You are approving preview version <code>{preview.previewVersionId}</code>
                </p>
              ) : null}
              {!isExternalProof ? (
                <label className="v10-cl-consent" data-testid="approval-consent">
                  <input
                    type="checkbox"
                    checked={consentAcknowledged}
                    onChange={(event) => setConsentAcknowledged(event.target.checked)}
                  />
                  I confirm this is the exact preview version shown and I consent to approval on this reference.
                </label>
              ) : null}
              <button
                type="button"
                className="v10-cl-btn v10-cl-btn--gold"
                disabled={!canApprove}
                onClick={onApprove}
              >
                {approveLabel}
              </button>
              <button
                type="button"
                className="v10-cl-btn"
                disabled={!canRequestChange}
                title={canRequestChange ? undefined : "Add your change request on the Changes tab first."}
                onClick={onRequestChange}
              >
                Request changes
              </button>
            </div>
          )}
        </div>
        <aside className="v10-cl-decision-box">
          <h3>What happens next?</h3>
          <p>After approval, your agency creates launch proof and prepares the handoff package.</p>
        </aside>
      </div>
    </section>
  );
}
